// frontend/src/pages/usuarios/UsuariosListPage.jsx
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PermissionDeniedAlert from '../../components/common/PermissionDeniedAlert';
import AlertException from '../../components/common/AlertException';
import AppSelect from '../../components/common/AppSelect';
import { useAuth } from '../../contexts/AuthContext';

const SEARCH_DEBOUNCE_MS = 450;

function UsuariosListPage() {
  const { user, loading: authLoading, hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // RBAC — permissões usadas nesta página
  const canView   = hasPermission('usuarios.view');
  const canCreate = hasPermission('usuarios.create');
  const canEdit   = hasPermission('usuarios.edit');
  const canDelete = hasPermission('usuarios.delete');

  const [usuarios, setUsuarios] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [paginationInfo, setPaginationInfo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // { kind: 'permission' } | AxiosError | null
  const [successMessage, setSuccessMessage] = useState('');
  const [resendModal, setResendModal] = useState({ open: false, user: null, sending: false, result: null });
  const abortRef = useRef(null);
  const perPageOptions = [
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 'all', label: 'Todos' },
  ];

  useEffect(() => {
    const createdUser = location.state?.userCreationSuccess;
    if (!createdUser?.name || !createdUser?.email) return;

    setSuccessMessage(`Usuário ${createdUser.name} - ${createdUser.email} cadastrado com sucesso e o mesmo já foi notificado por email.`);
    navigate(`${location.pathname}${location.search}`, { replace: true });
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (authLoading) return;

    const controller = new AbortController();
    abortRef.current = controller;

    const fetchUsuarios = async () => {
      setLoading(true);
      setError(null);

      // Short-circuit local se não tiver permissão
      if (!canView) {
        setError({ kind: 'permission' });
        setLoading(false);
        return;
      }

      try {
        const response = await api.get('/users', {
          signal: controller.signal,
          params: {
            page: currentPage,
            per_page: perPage === 'all' ? 10000 : Number(perPage),
            status: statusFilter,
            search: debouncedSearch || undefined,
          },
        });
        const data = response?.data?.data ?? [];
        setUsuarios(data);
        setPaginationInfo(response?.data?.meta ?? null);
      } catch (err) {
        if (controller.signal.aborted) return;
        if (err?.response?.status === 403) {
          setError({ kind: 'permission' });
        } else {
          setError(err);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchUsuarios();
    return () => controller.abort();
  }, [authLoading, canView, currentPage, debouncedSearch, perPage, statusFilter]);

  const handlePageChange = (newPage) => {
    if (!paginationInfo) return;
    if (newPage < 1 || newPage > paginationInfo.last_page) return;
    setCurrentPage(newPage);
  };

  const totalRegistrosPaginacao = Number.isFinite(Number(paginationInfo?.total))
    ? Number(paginationInfo.total)
    : usuarios.length;

  const handlePerPageChange = (value) => {
    setPerPage(value);
    setCurrentPage(1);
  };

  const handleDelete = async (id) => {
    if (!canDelete) return;

    if (window.confirm('Tem certeza de que deseja desativar este usuário?')) {
      try {
        await api.delete(`/users/${id}`);
        setUsuarios((prev) => prev.filter((u) => u.id !== id));
      } catch {
        // Tratamento simples na UI; sem log de console
        alert('Ocorreu um erro ao desativar o usuário.');
      }
    }
  };

  const handleRestore = async (id) => {
    if (!canDelete) return;

    if (window.confirm('Tem certeza de que deseja reativar este usuário?')) {
      try {
        await api.patch(`/users/${id}/restore`);
        setUsuarios((prev) => prev.filter((u) => u.id !== id));
      } catch {
        alert('Ocorreu um erro ao reativar o usuário.');
      }
    }
  };

  const handleResendWelcome = async () => {
    if (!resendModal.user) return;
    setResendModal((prev) => ({ ...prev, sending: true, result: null }));
    try {
      await api.post(`/users/${resendModal.user.id}/resend-welcome`);
      setResendModal((prev) => ({ ...prev, sending: false, result: { success: true, message: 'E-mail de boas-vindas reenviado com sucesso.' } }));
    } catch (err) {
      const msg = err?.response?.data?.message || 'Erro ao reenviar e-mail. Tente novamente.';
      setResendModal((prev) => ({ ...prev, sending: false, result: { success: false, message: msg } }));
    }
  };

  // Espera verificação de autenticação
  if (authLoading) {
    return (
      <div className="usuarios-page">
        <LoadingSpinner text="Verificando sessão..." />
      </div>
    );
  }

  // Loading da página
  if (loading) {
    return (
      <div className="usuarios-page">
        <LoadingSpinner text="Carregando usuários..." />
      </div>
    );
  }

  // Erros da página
  if (error) {
    if (error.kind === 'permission') {
      return (
        <div className="usuarios-page">
          <PermissionDeniedAlert
            requiredPermission="usuarios.view"
            action="visualizar a lista de usuários"
          />
        </div>
      );
    }
    return (
      <div className="usuarios-page">
        <AlertException error={error} fallback="Não foi possível carregar os usuários." />
      </div>
    );
  }

  return (
    <>
      <div className="usuarios-page">
      <div className="content-header">
        <h1 className="content-title">Usuários</h1>
        <p className="content-subtitle">Gerenciamento de usuários do sistema</p>
      </div>

      {successMessage && (
        <div className="alert alert-success" role="status">
          {successMessage}
        </div>
      )}

      <div className="card usuarios-list-card">
        <div className="card-body">
          <div className="list-toolbar usuarios-list-toolbar d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
            <div className="usuarios-filters-group d-flex align-items-center gap-3">
              <div className="input-group usuarios-search-group">
                <span className="input-group-text"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por nome, e-mail ou perfil..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    if (currentPage !== 1) {
                      setCurrentPage(1);
                    }
                  }}
                />
              </div>
              <div className="usuarios-status-filter d-flex align-items-center gap-2">
                <label htmlFor="statusFilter" className="mb-0 usuarios-status-label">Filtrar por:</label>
                <select
                  id="statusFilter"
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  aria-label="Filtrar usuários por status"
                >
                  <option value="active">Ativos</option>
                  <option value="inactive">Inativos</option>
                </select>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="text-secondary small">Registros por página</span>
                <AppSelect
                  className="usuarios-per-page-select"
                  ariaLabel="Registros por página"
                  options={perPageOptions}
                  value={perPage}
                  onChange={handlePerPageChange}
                />
              </div>
            </div>

            {canCreate && (
              <Link to="/painel/usuarios/novo" className="btn btn-primary">
                <i className="bi bi-plus-lg me-2"></i>
                Novo Usuário
              </Link>
            )}
          </div>

          <div className="table-responsive">
            <table className="table table-dark-custom table-stack">
              <thead>
                <tr>                  
                  <th>Nome</th>
                  <th>E-mail</th>
                  <th>Perfil</th>
                  <th>Órgão</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {usuarios.length > 0 ? (
                  usuarios.map((u) => {
                    const isProtected = u.id === 1 || u.id === user?.id;
                    const isInactive = Boolean(u.deleted_at);
                    const tooltipText =
                      u.id === 1
                        ? 'Não é possível alterar o super administrador'
                        : 'Não é possível alterar seu próprio usuário';

                    const canEditThis = canEdit && !isProtected && !isInactive;
                    const canDeleteThis = canDelete && !isProtected && !isInactive;

                    return (
                      <tr key={u.id}>                        
                        <td data-label="Nome">{u.name}</td>
                        <td data-label="E-mail">{u.email}</td>
                        <td data-label="Perfil">
                          {u.perfil ? (
                            <Link to={`/painel/perfis/${u.perfil.id}/visualizar`}>
                              {u.perfil.nome}
                            </Link>
                          ) : 'N/A'}
                        </td>
                        <td data-label="Órgão">{u.orgao ? u.orgao.sigla : 'N/A'}</td>
                        <td data-label="Status">{u.deleted_at ? 'Inativo' : 'Ativo'}</td>
                        <td className="table-actions usuarios-table-actions d-flex gap-2" data-label="Ações">
                          {canEdit && !isInactive && (
                            <Link
                              to={`/painel/usuarios/${u.id}/editar`}
                              className={`btn btn-sm btn-outline-warning ${!canEditThis ? 'disabled' : ''}`}
                              title={!canEditThis ? tooltipText : 'Editar'}
                              onClick={(e) => { if (!canEditThis) e.preventDefault(); }}
                            >
                              <i className="bi bi-pencil"></i>
                            </Link>
                          )}

                          {(canEdit || canCreate) && !isInactive && (
                            <button
                              className="btn btn-sm btn-outline-primary"
                              title="Reenviar e-mail de boas-vindas"
                              onClick={() => setResendModal({ open: true, user: u, sending: false, result: null })}
                            >
                              <i className="bi bi-envelope"></i>
                            </button>
                          )}

                          {canDelete && !isInactive && (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              title={!canDeleteThis ? tooltipText : 'Desativar'}
                              onClick={() => canDeleteThis && handleDelete(u.id)}
                              disabled={!canDeleteThis}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          )}

                          {canDelete && isInactive && (
                            <button
                              className="btn btn-sm btn-outline-success"
                              title="Reativar"
                              onClick={() => handleRestore(u.id)}
                            >
                              <i className="bi bi-person-check"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center">Nenhum usuário encontrado.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {paginationInfo && paginationInfo.last_page > 1 && perPage !== 'all' && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <span className="usuarios-pagination-summary">
                Página <strong>{paginationInfo.current_page}</strong> de{' '}
                <strong>{paginationInfo.last_page}</strong> •{' '}
                <strong>{totalRegistrosPaginacao.toLocaleString('pt-BR')}</strong> registros
              </span>
              <div className="btn-group">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <i className="bi bi-chevron-left"></i> Anterior
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === paginationInfo.last_page}
                >
                  Próximo <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>

      {resendModal.open && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Reenviar E-mail de Boas-vindas</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setResendModal({ open: false, user: null, sending: false, result: null })}
                  disabled={resendModal.sending}
                />
              </div>
              <div className="modal-body">
                {resendModal.result ? (
                  <div className={`alert ${resendModal.result.success ? 'alert-success' : 'alert-danger'} mb-0`}>
                    {resendModal.result.message}
                  </div>
                ) : (
                  <p className="mb-0">
                    Reenviar e-mail de boas-vindas para <strong>{resendModal.user?.name}</strong>?
                    {resendModal.user && !resendModal.user.email?.toLowerCase().endsWith('@sejus.df.gov.br') && (
                      <span className="d-block mt-2 text-warning small">
                        <i className="bi bi-exclamation-triangle me-1"></i>
                        Uma nova senha temporária será gerada e enviada por e-mail.
                      </span>
                    )}
                  </p>
                )}
              </div>
              <div className="modal-footer">
                {resendModal.result ? (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setResendModal({ open: false, user: null, sending: false, result: null })}
                  >
                    Fechar
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setResendModal({ open: false, user: null, sending: false, result: null })}
                      disabled={resendModal.sending}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleResendWelcome}
                      disabled={resendModal.sending}
                    >
                      {resendModal.sending ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Enviando...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-envelope me-1"></i>
                          Reenviar
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default UsuariosListPage;
